import Reveal from "@/components/Reveal";
import LifecycleSection from "@/components/sections/LifecycleSection";
import CTABand from "@/components/sections/CTABand";

const lifecycleImg =
  "https://static.prod-images.emergentagent.com/jobs/c1a075fa-8b15-42e2-9c19-a7a7f32ee730/images/a64a681dd4e255f56e016f47dd1cb36ec76d825824e9b700ff22bd94d00a423a.png";

const INPUTS = [
  {
    tag: "01",
    title: "Asset",
    desc: "Drug, molecule, brand, mechanism, or asset class.",
  },
  {
    tag: "02",
    title: "Indication",
    desc: "Therapeutic area, patient segment, geography, and treatment context.",
  },
  {
    tag: "03",
    title: "Lifecycle Stage",
    desc: "From clinical development to launch, access, and post-launch growth.",
  },
];

export default function Lifecycle() {
  return (
    <div data-testid="page-lifecycle">
      <section className="bg-ink-0 relative overflow-hidden">
        <div className="absolute inset-0 glow-soft pointer-events-none" aria-hidden="true" />
        <div className="container-edge pt-20 lg:pt-28 pb-12 relative z-10">
          <Reveal>
            <div className="eyebrow">The lifecycle</div>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-[5.4rem] leading-[1.04] tracking-tight mt-4 text-parchment-bright max-w-4xl">
              One patient-evidence method.
              <span className="block italic text-parchment">Calibrated to your asset stage.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-parchment/85 text-lg leading-relaxed">
              The patient voice needed in clinical development is different from the voice needed at launch, HTA review, payer engagement, or post-launch growth.
            </p>
            <p className="mt-5 max-w-2xl text-parchment/75 text-lg leading-relaxed">
              Voice2Evidence scopes each engagement to your asset, indication, geography, and lifecycle stage — then translates patient voice into evidence-ready strategy for the teams that need to act.
            </p>
          </Reveal>
        </div>
        <div className="container-edge relative z-10 pb-8">
          <Reveal>
            <div className="card-dark p-0 overflow-hidden">
              <img
                src={lifecycleImg}
                alt="Lifecycle timeline"
                className="w-full aspect-[5/2] object-cover"
                style={{ filter: "invert(1) hue-rotate(180deg) saturate(0.85) brightness(0.95)" }}
                data-testid="lifecycle-hero-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* How an Engagement Starts */}
      <section className="bg-ink-2" data-testid="lifecycle-flow-section">
        <div className="container-edge py-20 lg:py-24">
          <Reveal>
            <div className="grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8">
                <div className="eyebrow">How an engagement starts</div>
                <h2 className="font-serif font-normal text-3xl md:text-5xl tracking-tight mt-4 text-parchment-bright max-w-3xl leading-tight">
                  Three inputs from you. Patient-voice evidence back.
                </h2>
              </div>
              <div className="col-span-12 lg:col-span-4">
                <p className="text-parchment/75 leading-relaxed">
                  You share the asset, indication, and lifecycle stage. We return patient-voice insights and strategic recommendations mapped to the functions that matter most.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Inputs */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08]">
            {INPUTS.map((it, i) => (
              <Reveal
                key={it.tag}
                delay={i * 80}
                className="bg-ink-1 p-7 card-dark-hover"
                data-testid={`lifecycle-input-${it.tag}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rust">
                  Input {it.tag}
                </div>
                <h3 className="font-serif text-[1.5rem] text-parchment-bright mt-4 leading-snug">
                  {it.title}
                </h3>
                <p className="mt-3 text-[13.5px] text-parchment/70 leading-relaxed">{it.desc}</p>
              </Reveal>
            ))}
          </div>

          {/* Arrow */}
          <Reveal className="mt-10 flex items-center justify-center">
            <div className="font-mono text-rust text-2xl tracking-[0.4em]">↓</div>
          </Reveal>

          {/* Output */}
          <Reveal delay={120}>
            <div className="mt-10 border border-rust/40 bg-ink-1 p-8 lg:p-10" data-testid="lifecycle-output">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rust">Output</div>
              <p className="font-serif text-2xl md:text-[1.85rem] text-parchment-bright mt-4 leading-snug max-w-4xl">
                Patient voice translated into strategy for Medical Affairs, HEOR, Market Access, Regulatory Affairs, Commercial, Patient Engagement, and Leadership.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <LifecycleSection />

      {/* Best Fit */}
      <section
        className="bg-ink-2 relative border-t border-white/[0.06]"
        data-testid="lifecycle-best-fit"
      >
        <div className="container-edge py-20 lg:py-24">
          <Reveal>
            <div className="grid grid-cols-12 gap-x-8 gap-y-8 items-start">
              <div className="col-span-12 lg:col-span-4">
                <div className="eyebrow">Best fit</div>
              </div>
              <div className="col-span-12 lg:col-span-8">
                <p className="font-serif text-2xl md:text-[2rem] text-parchment-bright leading-snug max-w-3xl">
                  Voice2Evidence works across all five stages.
                </p>
                <p className="mt-5 text-parchment/75 text-[16.5px] leading-relaxed max-w-3xl">
                  Impact is strongest at <span className="text-parchment-bright">late-stage, launch, and post-launch</span>, where patient voice directly shapes evidence, access, medical, and engagement decisions in a defined commercial window.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
